import React, { useMemo } from 'react';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE as MetricType } from 'src/utils/entityDefinitions';
import GenericChart from 'src/components/Charts/GenericChart';
import { createNonComparisonLegendDivs } from 'src/utils/chartUtils';
import { getTooltipWithHeaderAndValueDiv } from 'src/components/EntityPage/Renderer/tooltipDiv';
import moment from 'moment';
import { convertBrandedSearchRateOverTimeDataToCSV } from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/AudiencesTab/BrandedSearchRate/utils';
import {
  BrandedSearchRateLineChartProps,
  GenericChartParameters,
  MetricDataForTrendChart
} from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/AudiencesTab/BrandedSearchRate/types';

/**
 *
 * @returns Line chart displaying New to Brand metrics
 */
const BrandedSearchRateLineChart = ({ data, legendValue }: BrandedSearchRateLineChartProps) => {
  const { app, mainTimePeriod, comparisonTimePeriod } = useAppSelector((state) => state);

  const formatMetric = useMetricFormatter();
  const chartProps: Highcharts.Options = useMemo(
    () => ({
      chart: {
        type: 'areaspline',
        height: 500
      },
      title: {
        text: 'Branded Search Rate',
        x: -10
      },
      subtitle: {
        text: '',
        x: -10,
        style: {
          height: '51px'
        }
      },
      plotOptions: {
        events: {
          legendItemClick() {
            // Disable legend click to avoid hiding the single series
            return false;
          }
        },
        fillEnabled: true,
        series: {
          states: {
            hover: {
              enabled: true
            }
          }
        }
      },
      legend: {
        labelFormatter() {
          const label = `Branded Search Rate (${formatMetric(legendValue, MetricType.PERCENT, {
            showFullValue: true,
            decimalPlaces: 2
          })})`;

          const legendDivs: string[] = [];
          createNonComparisonLegendDivs({
            chartDisplayTimePeriod: mainTimePeriod,
            mainLegendMetricValue: {
              value: label
            },
            isSameMetricComparison: true,
            metricsDataByTimeSeries: null,
            legendDivs
          });

          return legendDivs[0];
        }
      },
      tooltip: {
        enabled: true,
        shared: true,
        formatter() {
          const toolTipValue = formatMetric(this.points[0].y / 100, MetricType.PERCENT, {
            showFullValue: true,
            decimalPlaces: 2
          });
          const timeStamp = moment.utc(this.points[0].x).format('MMM D');
          return getTooltipWithHeaderAndValueDiv({ headerContent: timeStamp, mainContent: toolTipValue });
        }
      },
      useUTC: false,
      xAxis: [
        {
          type: 'datetime',
          dateTimeLabelFormats: {
            day: '%b %e'
          }
        }
      ],
      yAxis: [
        {
          labels: {
            formatter() {
              return `${this.value}%`;
            }
          }
        }
      ],
      exporting: {
        enabled: true,
        showCompare: false
      }
    }),
    [formatMetric, legendValue, mainTimePeriod]
  );

  const metricData: MetricDataForTrendChart = useMemo(
    () => ({
      metricType: 'PERCENT',
      marker: {
        enabled: false
      },
      events: {},
      color: '#46a8f6',
      point: { events: {} },
      customEvents: { series: {}, point: {} },
      data,
      name: 'Blah'
    }),
    [data]
  );

  const chartParams: GenericChartParameters = {
    app,
    chartProps,
    chartSeries: [metricData],
    comparisonTimePeriod
  };

  return (
    <GenericChart
      convertSeriesToDelimitedData={() =>
        convertBrandedSearchRateOverTimeDataToCSV({
          data,
          dateRange: mainTimePeriod.displayName
        })
      }
      {...chartParams}
    />
  );
};

export default BrandedSearchRateLineChart;
